import {
  Warning as WarningIcon,
  Storage as StorageIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Build as BuildIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutlineOutlined as ErrorOutlineIcon,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import React, { useState } from 'react';

import { log } from '../utils/errors/logger';
import {
  useGetSystemStatus,
  useCleanupAllData,
  useFixDuplicateACL,
  useRecreateQdrantCollection,
} from '@/hooks/api/config/useServerAdmin';

const DataManagement: React.FC = () => {
  // const { t } = useTranslation(); // Uncomment when translations are added
  const { data: systemStatus, isLoading, refetch } = useGetSystemStatus();
  const cleanupAllData = useCleanupAllData();
  const fixDuplicateACL = useFixDuplicateACL();
  const recreateQdrant = useRecreateQdrantCollection();

  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'warning'; message: string } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; title: string; message: string; action: () => void }>({
    open: false,
    title: '',
    message: '',
    action: () => {},
  });

  const handleCleanupAll = async () => {
    try {
      const result = await cleanupAllData.mutateAsync();
      setAlert({
        type: 'success',
        message: `Cleanup completed: MongoDB ${result.mongodb_deleted}, Qdrant ${result.qdrant_deleted}, S3 ${result.s3_deleted}.`
      });
    } catch (error) {
      log.error('Error during cleanup', { error });
      setAlert({ type: 'error', message: 'Failed to cleanup data' });
    }
  };

  const handleFixDuplicateACL = async () => {
    try {
      const result = await fixDuplicateACL.mutateAsync();
      setAlert({
        type: 'success',
        message: `ACL cleanup completed: MongoDB ${result.mongodb_deleted}, Qdrant ${result.qdrant_deleted}, S3 ${result.s3_deleted}.`
      });
    } catch (error) {
      log.error('Error fixing duplicate ACL', { error });
      setAlert({ type: 'error', message: 'Failed to fix duplicate ACL entries' });
    }
  };

  const handleRecreateQdrant = async () => {
    try {
      await recreateQdrant.mutateAsync('smartdoc');
      setAlert({ type: 'success', message: 'Qdrant collection recreated successfully' });
    } catch (error) {
      log.error('Error recreating Qdrant collection', { error });
      setAlert({ type: 'error', message: 'Failed to recreate Qdrant collection' });
    }
  };

  const showConfirmDialog = (title: string, message: string, action: () => void) => {
    setConfirmDialog({ open: true, title, message, action });
  };

  const handleConfirmAction = () => {
    confirmDialog.action();
    setConfirmDialog({ open: false, title: '', message: '', action: () => {} });
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        <StorageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Data Management
      </Typography>

      {alert && (
        <Alert
          severity={alert.type}
          onClose={() => setAlert(null)}
          sx={{ mb: 2 }}
        >
          {alert.message}
        </Alert>
      )}

      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">System Status</Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            Refresh
          </Button>
        </Box>

        {systemStatus ? (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Overview</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Status:</strong> {systemStatus.status}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Version:</strong> {systemStatus.version}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Uptime:</strong> {systemStatus.uptime}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Total users:</strong> {systemStatus.total_users}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total documents:</strong> {systemStatus.total_documents}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Service Health</Typography>
                  <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                    <Typography variant="body2">Database</Typography>
                    <Chip
                      icon={systemStatus.database_connected ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
                      label={systemStatus.database_connected ? 'Connected' : 'Disconnected'}
                      color={systemStatus.database_connected ? 'success' : 'error'}
                      size="small"
                    />
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                    <Typography variant="body2">Redis</Typography>
                    <Chip
                      icon={systemStatus.redis_connected ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
                      label={systemStatus.redis_connected ? 'Connected' : 'Disconnected'}
                      color={systemStatus.redis_connected ? 'success' : 'error'}
                      size="small"
                    />
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="body2">Qdrant</Typography>
                    <Chip
                      icon={systemStatus.qdrant_connected ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
                      label={systemStatus.qdrant_connected ? 'Connected' : 'Disconnected'}
                      color={systemStatus.qdrant_connected ? 'success' : 'error'}
                      size="small"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography variant="body1" color="text.secondary">
              {isLoading ? 'Loading system status...' : 'No system status data available'}
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 4 }} />

      <Typography variant="h5" mb={2}>Data Operations</Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <BuildIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Fix Issues
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Fix common data issues and inconsistencies.
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleFixDuplicateACL}
                disabled={fixDuplicateACL.isPending || isLoading}
                sx={{ mr: 1, mb: 1 }}
              >
                Fix Duplicate ACL Entries
              </Button>
              <Button
                variant="outlined"
                color="warning"
                onClick={() => showConfirmDialog(
                  'Recreate Qdrant Collection',
                  'This will delete all vector data and recreate the collection. Are you sure?',
                  handleRecreateQdrant
                )}
                disabled={recreateQdrant.isPending || isLoading}
                sx={{ mb: 1 }}
              >
                Recreate Qdrant Collection
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} color="error" />
                Danger Zone
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Destructive operations that cannot be undone.
              </Typography>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => showConfirmDialog(
                  'Delete All Data',
                  'This will permanently delete all documents, groups, ACL entries, audit logs, and S3 files. This action cannot be undone!',
                  handleCleanupAll
                )}
                disabled={cleanupAllData.isPending || isLoading}
              >
                Delete All Data (MongoDB + Qdrant + S3)
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {(isLoading || cleanupAllData.isPending || fixDuplicateACL.isPending || recreateQdrant.isPending) && (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress />
        </Box>
      )}

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>
            Cancel
          </Button>
          <Button onClick={handleConfirmAction} color="error" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataManagement;
